import React, { useState, useRef } from 'react';
import { placeOrder, setGiftWrap } from './auth';
import WalletPay from './WalletPay';
import { orderCtaLabel } from './payment-cta';
import { needsPhoneVerify, verifyMethodLabel } from './CodPhoneVerify';
import LegalGuarantee from './LegalGuarantee';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};

/**
 * Jump to the COD verification panel — it sits up in the payment list, well above
 * the place-order button that reports the block. Flashes it so the eye lands on
 * the right row, then focuses the send link (also the keyboard-only path).
 */
function scrollToVerify(e) {
  if (e) e.preventDefault();
  if (typeof document === 'undefined') return;
  const el = document.getElementById('cn-pv-panel');
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('is-flash');
  window.setTimeout(() => el.classList.remove('is-flash'), 1800);
  const link = el.querySelector('.cn-pv__sendlink');
  if (link) {
    window.setTimeout(() => {
      try { link.focus({ preventScroll: true }); } catch (err) { link.focus(); }
    }, 450); // after the smooth scroll, so focus doesn't yank the viewport
  }
}

// Final actions, rendered at the bottom of the (now last) payment step. The old
// standalone "Επισκόπηση & ολοκλήρωση" review step was removed; this keeps the
// gift-wrap add-on, terms/marketing opt-ins and place-order (creates the WC order
// + runs the gateway). Address/shipping recap dropped — the sidebar summary + the
// editable step-1 cover that.
export default function Step3Review({ value, payment, payments, cart, onCart, loading, phoneVerified }) {
  const alreadyOptedIn = !!cfg.marketingOptedIn; // logged-in + already subscribed → hide the box
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(alreadyOptedIn);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsHtml, setTermsHtml] = useState(null); // null = not yet fetched

  function openTerms(e) {
    if (e) e.preventDefault();
    setTermsOpen(true);
    if (termsHtml === null) {
      fetch(`${cfg.ajaxUrl}?action=cn_terms_html`, { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((res) => setTermsHtml((res && res.data && res.data.html) || ''))
        .catch(() => setTermsHtml(''));
    }
  }
  const [giftOptimistic, setGiftOptimistic] = useState(null); // instant checkbox state; null = use server truth
  const [giftBusy, setGiftBusy] = useState(false); // request in flight → block place-order like a payment change
  const giftSeq = useRef(0); // guards against out-of-order responses from rapid toggles

  const gift = (cart && cart.giftWrap) || null;
  const giftSelected = giftOptimistic !== null ? giftOptimistic : !!(gift && gift.selected);
  async function toggleGift(on) {
    const seq = ++giftSeq.current;
    setGiftOptimistic(on); // flip the checkbox immediately — totals reconcile when the request returns
    setGiftBusy(true);
    const { ok, data } = await setGiftWrap(on);
    if (seq !== giftSeq.current) return; // a newer toggle superseded this one — the latest clears busy
    if (ok && data.cart && onCart) onCart(data.cart);
    setGiftOptimistic(null);
    setGiftBusy(false);
  }

  const b = value.billing || {};
  const inv = value.invoice || {};
  // Nothing payable (e.g. wallet covers 100%) → no gateway/express button; just place.
  const noPayment = !!(cart && cart.needsPayment === false);
  const isWallet = payment === 'cn_stripe_wallet' && !noPayment;
  // Why the complete-order button is greyed out (shown on hover / tap). The pay-later
  // methods — αντικαταβολή and πληρωμή κατά την παραλαβή — need the SMS-verified mobile
  // first: the server refuses the order without it, so block here rather than let the
  // customer hit a late error.
  // …unless there's nothing to pay on delivery in the first place (wallet credit
  // covers 100%), where a leftover `cod` selection is never actually charged.
  const needsPhone = !noPayment && needsPhoneVerify(payment) && !phoneVerified;
  const TERMS_BLOCK = 'Αποδέξου τους όρους & προϋποθέσεις για να ολοκληρώσεις την παραγγελία.';
  const PHONE_BLOCK = `Επιβεβαίωσε το κινητό σου για να ολοκληρώσεις με ${verifyMethodLabel(payment)} εδώ.`;
  // Plain string for the button's title/tooltip; the rendered version below carries
  // the «εδώ» link, because the panel that fixes it is further up the page.
  const blockReason = !terms ? TERMS_BLOCK : needsPhone ? PHONE_BLOCK : '';
  const blockMessage = (terms && needsPhone) ? (
    <>
      Επιβεβαίωσε το κινητό σου για να ολοκληρώσεις με {verifyMethodLabel(payment)}{' '}
      <button type="button" className="cn-linkbtn" onClick={scrollToVerify}>εδώ</button>.
    </>
  ) : blockReason;
  const locker = value.boxnowLocker;

  const buildPayload = () => ({
    docType: value.docType,
    invoice: inv,
    billing: b,
    shipToBilling: value.shipToBilling,
    shipping: value.shipping || {},
    email: b.email || '',
    contactPhone: value.contactPhone || '',
    shippingMethod: value.shippingMethod || '',
    boxnowLocker: locker || null,
    deliveryNote: value.deliveryNote || '',
    payment,
    terms,
    marketing,
  });

  async function submit() {
    setError('');
    setPlacing(true);
    const { ok, data } = await placeOrder(buildPayload());
    if (ok && data.redirect) {
      window.location.href = data.redirect;
      return;
    }
    setPlacing(false);
    setError(data.message || 'Κάτι πήγε στραβά. Δοκίμασε ξανά.');
  }

  return (
    <div className="cn-finalize">
      {gift && (
        <div className="cn-review__row cn-addons">
          <div className="cn-review__head"><span className="cn-review__title">Πρόσθετες υπηρεσίες</span></div>
          <label className={`cn-addon ${gift.available ? '' : 'is-unavailable'} ${giftSelected ? 'is-selected' : ''}`}>
            <input
              type="checkbox"
              checked={giftSelected}
              disabled={!gift.available}
              onChange={(e) => toggleGift(e.target.checked)}
            />
            <svg className="cn-addon__ico" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 12v9H4v-9" /><path d="M2 7h20v5H2z" /><path d="M12 22V7" />
              <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
            </svg>
            <span className="cn-addon__body">
              <span className="cn-addon__title">Συσκευασία δώρου <span className="cn-addon__fee">+{gift.fee}</span></span>
              {gift.available ? (
                <span className="cn-addon__desc">Τυλίγουμε την παραγγελία σου σε συσκευασία δώρου.</span>
              ) : gift.codBlocked ? (
                <span className="cn-addon__desc cn-addon__desc--na">Δεν είναι διαθέσιμη με αντικαταβολή.</span>
              ) : (
                <span className="cn-addon__desc cn-addon__desc--na">Διαθέσιμη μόνο για παραγγελίες έως {String(gift.maxKg).replace('.', ',')} κιλά.</span>
              )}
            </span>
          </label>
        </div>
      )}

      <label className="cn-terms">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
        <span>
          Έχω διαβάσει και αποδέχομαι τους{' '}
          <button type="button" className="cn-linkbtn" onClick={openTerms}>όρους &amp; προϋποθέσεις</button>.
        </span>
      </label>

      {termsOpen && (
        <div className="cn-terms-modal" role="dialog" aria-modal="true" aria-label={cfg.termsTitle || 'Όροι & Προϋποθέσεις'} onClick={() => setTermsOpen(false)}>
          <div className="cn-terms-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="cn-terms-modal__head">
              <span className="cn-terms-modal__title">{cfg.termsTitle || 'Όροι & Προϋποθέσεις'}</span>
              <button type="button" className="cn-terms-modal__close" aria-label="Κλείσιμο" onClick={() => setTermsOpen(false)}>✕</button>
            </div>
            {termsHtml === null
              ? <div className="cn-terms-modal__body cn-terms-modal__body--loading"><span className="cn-btn__spinner" aria-hidden="true" /> Φόρτωση…</div>
              : termsHtml
                ? <div className="cn-terms-modal__body" dangerouslySetInnerHTML={{ __html: termsHtml }} />
                : <div className="cn-terms-modal__body"><p>Δες τους <a href={cfg.termsUrl || '/oroi-xrisis/'} target="_blank" rel="noopener noreferrer">όρους &amp; προϋποθέσεις</a>.</p></div>}
            <div className="cn-terms-modal__foot">
              <button type="button" className="cn-btn cn-btn--primary" onClick={() => { setTerms(true); setTermsOpen(false); }}>Αποδοχή &amp; κλείσιμο</button>
            </div>
          </div>
        </div>
      )}

      <LegalGuarantee />

      {!alreadyOptedIn && (
        <label className="cn-terms cn-terms--optional">
          <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
          <span>Θέλω να λαμβάνω προσφορές και νέα του καταστήματος.</span>
        </label>
      )}

      {error && <div className="cn-errors" role="alert"><ul><li>{error}</li></ul></div>}

      {isWallet ? (
        <div className="cn-step-nav cn-step-nav--wallet">
          {!terms ? (
            <span className="cn-review__muted">Αποδέξου τους όρους για να πληρώσεις με Apple Pay / Google Pay.</span>
          ) : (loading || giftBusy) ? (
            <span className="cn-review__muted">Ενημέρωση…</span>
          ) : (
            <WalletPay amount={(cart && cart.totalMinor) || 0} buildPayload={buildPayload} />
          )}
        </div>
      ) : (
        <div className="cn-step-nav">
          <button
            type="button"
            className={`cn-btn cn-btn--primary cn-next ${blockReason ? 'is-blocked' : ''}`}
            disabled={placing || loading || giftBusy}
            title={blockReason || undefined}
            aria-disabled={!!blockReason || !!loading || !!giftBusy}
            onClick={() => { if (loading || giftBusy) return; if (blockReason) { setError(blockMessage); return; } submit(); }}
          >
            {(placing || loading || giftBusy) && <span className="cn-btn__spinner" aria-hidden="true" />}
            {placing ? 'Ολοκλήρωση…' : (loading || giftBusy) ? 'Ενημέρωση…' : (noPayment ? 'Ολοκλήρωση παραγγελίας' : orderCtaLabel(payment, payments))}
          </button>
        </div>
      )}
    </div>
  );
}
